import { useState } from 'react';
import { CreditCard, Settings, User, LogOut, Bell, Search, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { WorkspaceSelector } from './workspace/WorkspaceSelector';
import { Link } from 'react-router-dom';

export function Header() {
  const { user, currentWorkspace, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [hasNotifications] = useState(true); // This would be dynamic in a real app

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto w-full">
        <div className="flex flex-wrap justify-between items-center h-16 px-4 sm:px-6 lg:px-8 gap-x-4 gap-y-2 xl:gap-x-8 2xl:gap-x-16">
          {/* Left section */}
          <div className="flex items-center gap-4 sm:gap-6 lg:gap-8 xl:gap-10 2xl:gap-12 min-w-fit flex-grow-0 flex-shrink-0">
            <Link to="/" className="flex items-center gap-2 min-w-0">
              {currentWorkspace?.logo ? (
                <img
                  src={currentWorkspace.logo}
                  alt="Logo"
                  className="h-8 w-8 rounded"
                />
              ) : (
                <Building2 className="h-8 w-8 text-green-600" />
              )}
              <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent truncate">
                FreelancePay
              </span>
            </Link>
            <WorkspaceSelector />
          </div>

          {/* Center section - Search */}
          <div className="hidden md:flex flex-1 justify-center px-2 lg:px-6 xl:px-12 2xl:px-20 min-w-0">
            <div className="w-full max-w-lg xl:max-w-2xl 2xl:max-w-3xl">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search invoices, clients..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg 
                           text-sm placeholder-gray-400 focus:outline-none focus:ring-2 
                           focus:ring-green-500 focus:border-transparent bg-gray-50"
                />
              </div>
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-4 sm:gap-6 lg:gap-8 xl:gap-10 2xl:gap-12 min-w-0">
            {/* Mobile search button */}
            <button 
              className="md:hidden text-gray-500 hover:text-gray-600"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Notifications */}
            <button className="relative text-gray-500 hover:text-gray-600 focus:outline-none">
              <Bell className="h-5 w-5" />
              {hasNotifications && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white" />
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 text-gray-700 hover:text-gray-900 focus:outline-none min-w-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-green-50 rounded-full flex items-center justify-center ring-2 ring-green-50">
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.firstName}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <User className="h-5 w-5 text-green-600" />
                    )}
                  </div>
                  <span className="hidden sm:block text-sm font-medium truncate">
                    {user?.firstName}
                  </span>
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg py-2 ring-1 ring-black ring-opacity-5">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="font-medium text-gray-900">{user?.firstName} {user?.lastName}</div>
                    <div className="text-sm text-gray-500 truncate">{user?.email}</div>
                  </div>
                  
                  <div className="py-1">
                    <Link
                      to="/app/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 items-center"
                    >
                      <Settings className="h-4 w-4 mr-3 text-gray-400" />
                      Settings
                    </Link>

                    <Link
                      to="/app/settings/workspaces"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 items-center"
                    >
                      <CreditCard className="h-4 w-4 mr-3 text-gray-400" />
                      Manage Workspaces
                    </Link>
                  </div>

                  <div className="border-t border-gray-100">
                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 items-center"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile search bar */}
        {showSearch && (
          <div className="p-4 border-t border-gray-200 md:hidden">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search invoices, clients..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg 
                         text-sm placeholder-gray-400 focus:outline-none focus:ring-2 
                         focus:ring-green-500 focus:border-transparent bg-gray-50"
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}